import type { ProductItem } from 'src/components/product-page/context/types/product.type'

type Location = {
  search: string
  pathname: string
}

export const getPriceRange = (items: ProductItem[]) => {
  const prices: number[] = []
  const listPrices: number[] = []

  items.forEach((item) =>
    item.sellers.forEach((seller) => {
      if (seller.commertialOffer.AvailableQuantity > 0) {
        prices.push(seller.commertialOffer.Price)
        listPrices.push(seller.commertialOffer.ListPrice)
      }
    })
  )

  if (prices.length === 0) {
    prices.push(0)
  }

  if (listPrices.length === 0) {
    listPrices.push(0)
  }

  return {
    sellingPrice: {
      highPrice: Math.max(...prices),
      lowPrice: Math.min(...prices),
    },
    listPrice: {
      highPrice: Math.max(...listPrices),
      lowPrice: Math.min(...listPrices),
    },
  }
}

const findSkuByColor = (items: ProductItem[], color: string) => {
  return (
    items.find(
      (product) =>
        product.Cor?.[0].split('-')?.[0].toLocaleLowerCase() ===
          color.toLocaleLowerCase() && product.isAvailable
    )?.itemId ??
    items.filter(
      (product) =>
        product.Cor?.[0].split('-')?.[0].toLocaleLowerCase() ===
        color.toLocaleLowerCase()
    )[0]?.itemId
  )
}

const getSkuByColorParameter = (items: ProductItem[], location: Location) => {
  const params = new URLSearchParams(location.search)
  const urlHasColorInParameter = params.get('aSku')

  if (urlHasColorInParameter) {
    const [, color] = urlHasColorInParameter.split(':')

    return color ? findSkuByColor(items, color) : null
  }

  return null
}

const getSkuByUrlParameter = (items: ProductItem[], location: Location) => {
  const itemId = location.pathname?.split('/p')?.[0]?.split('-')?.pop()

  return items.find((item) => item.itemId === itemId)?.itemId ?? null
}

const getSkuWithLowestPrice = (items: ProductItem[]) => {
  const priceRange = getPriceRange(items)

  return items.find((item) =>
    item.sellers.find(
      (seller) =>
        seller.sellerDefault &&
        seller.commertialOffer.AvailableQuantity > 0 &&
        seller.commertialOffer.Price === priceRange.sellingPrice.lowPrice
    )
  )?.itemId
}

const getSkuWithAvailability = (items: ProductItem[]) => {
  return (
    items.find((item) =>
      item.sellers.find(
        (seller) => seller.commertialOffer.AvailableQuantity > 0
      )
    )?.itemId ?? items[0].itemId
  )
}

export const getInitialItem = (items: ProductItem[], location: Location) => {
  const skuByColorParameter = getSkuByColorParameter(items, location)
  const skuByUrlParameter = getSkuByUrlParameter(items, location)
  const skuWithLowestPrice = getSkuWithLowestPrice(items)
  const skuWithAvailability = getSkuWithAvailability(items)

  const sku =
    skuByColorParameter ??
    skuByUrlParameter ??
    skuWithLowestPrice ??
    skuWithAvailability

  return items.find((item) => item.itemId === sku) ?? items[0]
}
